import { useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowDownLeft, ArrowUpRight, BadgeCheck, Radio } from "lucide-react";

const inr = (n) => `₹${Number(Math.round(n) || 0).toLocaleString("en-IN")}`;

/**
 * HolographicLedgerRow
 * ────────────────────
 * A 3D-tilted glassmorphism card that emits a soft cyan glow on debit
 * and a gold glow on credit. Hovering the card tracks pointer position
 * to compute a subtle X/Y rotation. Tapping the Data-Ping icon pulses
 * an outward ring.
 */
export function HolographicLedgerRow({ row, tier }) {
  const isCredit = row.type === "credit";
  const glow = isCredit ? tier.accent : "#2FB999";
  const label = isCredit ? "Credit" : "Debit";

  const cardRef = useRef(null);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const springX = useSpring(rx, { stiffness: 240, damping: 22 });
  const springY = useSpring(ry, { stiffness: 240, damping: 22 });
  const rotateX = useTransform(springY, [-0.5, 0.5], [6, -6]);
  const rotateY = useTransform(springX, [-0.5, 0.5], [-8, 8]);

  const [ping, setPing] = useState(0);

  const handleMove = (e) => {
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    rx.set((e.clientX - rect.left) / rect.width - 0.5);
    ry.set((e.clientY - rect.top) / rect.height - 0.5);
  };
  const handleLeave = () => { rx.set(0); ry.set(0); };

  return (
    <motion.div
      ref={cardRef}
      data-testid={`wallet-tx-${row.tx_id}`}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      style={{
        rotateX, rotateY, transformPerspective: 1200,
        background:
          "linear-gradient(135deg,rgba(255,255,255,0.045),rgba(255,255,255,0.015))",
        border: `1px solid ${glow}22`,
        boxShadow: `0 24px 60px -30px ${glow}55, inset 0 1px 0 rgba(255,255,255,0.05)`,
        backdropFilter: "blur(14px)",
      }}
      whileHover={{
        boxShadow: `0 30px 80px -30px ${glow}88, inset 0 1px 0 rgba(255,255,255,0.08)`,
        borderColor: `${glow}55`,
      }}
      transition={{ type: "spring", stiffness: 200, damping: 20 }}
      className="rounded-sm px-4 py-3 grid grid-cols-[auto_1fr_auto_auto_auto] items-center gap-4 relative overflow-hidden"
    >
      {/* type icon + data-ping */}
      <div className="flex flex-col items-center gap-1.5">
        <div
          className="w-8 h-8 rounded-full grid place-items-center"
          style={{
            background: `${glow}18`,
            border: `1px solid ${glow}55`,
            boxShadow: `0 0 18px ${glow}44`,
          }}
        >
          {isCredit
            ? <ArrowDownLeft size={13} style={{ color: glow }} />
            : <ArrowUpRight size={13} style={{ color: glow }} />}
        </div>
        <button
          type="button"
          onClick={() => setPing((n) => n + 1)}
          data-testid={`wallet-tx-ping-${row.tx_id}`}
          className="relative w-4 h-4 grid place-items-center group"
          title="Data-Ping"
        >
          <Radio size={11} style={{ color: glow }} className="opacity-70 group-hover:opacity-100" />
          {[...Array(3)].map((_, i) => (
            <motion.span
              key={`${ping}-${i}`}
              aria-hidden
              className="absolute inset-0 rounded-full"
              style={{ border: `1px solid ${glow}` }}
              initial={{ scale: 0.4, opacity: 0.9 }}
              animate={{ scale: 3.4, opacity: 0 }}
              transition={{ duration: 1.2, delay: i * 0.18, ease: "easeOut" }}
            />
          ))}
        </button>
      </div>

      {/* description */}
      <div className="min-w-0">
        <div
          className="font-grotesk text-[9px] uppercase tracking-[0.28em]"
          style={{ color: glow }}
        >
          {label}
        </div>
        <div className="text-[13px] text-[#F5F5F0] font-body truncate">
          {row.description}
        </div>
        <div className="text-[10px] text-[#6f6f68] font-mono mt-0.5">
          {row.tx_id} · {new Date(row.created_at).toLocaleString()}
        </div>
      </div>

      {/* amount */}
      <div className="text-right">
        <div
          className="font-mono text-sm tracking-tight"
          style={{ color: glow, textShadow: `0 0 18px ${glow}66` }}
        >
          {isCredit ? "+" : "−"}{inr(row.amount)}
        </div>
        <div className="text-[10px] text-[#6f6f68] font-mono">
          bal {inr(row.balance_after)}
        </div>
      </div>

      {/* signature status */}
      <div className="text-center">
        {row.signature_valid ? (
          <BadgeCheck size={14} className="text-[#2FB999] inline" />
        ) : (
          <span className="text-[#C0392B] text-[9px] font-grotesk uppercase tracking-[0.22em]">
            Tampered
          </span>
        )}
      </div>

      {/* left glow strip */}
      <div
        className="absolute inset-y-0 left-0 w-[3px]"
        style={{
          background: `linear-gradient(180deg, transparent, ${glow}, transparent)`,
          boxShadow: `0 0 14px ${glow}`,
        }}
      />
    </motion.div>
  );
}
